import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { WithTooltip } from '../../../ui/tooltip'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../../ui/dropdown-menu'
import type { PageHistoryItem } from '../TitlebarContext'
import { ProjectTabIcon } from '../../../common/ProjectIcon'

/** So lange gedrückt, und die Liste geht auf statt des Sprungs (wie Safari). */
export const HISTORY_HOLD_MS = 450
/** Der Tooltip kommt spät — die Pfeile erklären sich, er soll beim Klicken nicht stören (Sven, 04.10.2026). */
export const HISTORY_TOOLTIP_DELAY_MS = 1500

export interface HistoryArrowsProps {
  canGoBack: boolean
  canGoForward: boolean
  /** Die Seiten davor, nächste zuerst. */
  backList: PageHistoryItem[]
  /** Die Seiten danach, nächste zuerst. */
  forwardList: PageHistoryItem[]
  onBack: () => void
  onForward: () => void
  /** Sprung aus der Liste an eine Stelle des Seitenverlaufs. */
  onGoTo: (index: number) => void
}

const Chevron = ({ direction }: { direction: 'back' | 'forward' }) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d={direction === 'back' ? 'M10 3.5 5.5 8l4.5 4.5' : 'M6 3.5 10.5 8 6 12.5'} />
  </svg>
)

/** Das Icon der Seite wie im Tab — Projekte mit ihrem eigenen (gewählt 04.10.2026 statt Farbpunkt). */
const PageIcon = ({ item }: { item: PageHistoryItem }) => {
  const Icon = item.icon
  const fallback = Icon ? <Icon size={16} /> : null
  return (
    <span className="titlebar__history-icon" aria-hidden="true">
      {item.projectId ? <ProjectTabIcon projectId={item.projectId} size={16} fallback={fallback} /> : fallback}
    </span>
  )
}

interface ArrowProps {
  direction: 'back' | 'forward'
  enabled: boolean
  list: PageHistoryItem[]
  onStep: () => void
  onGoTo: (index: number) => void
}

/**
 * Ein Pfeil: Klick springt eins, **Rechtsklick oder Halten** öffnet die Liste der Seiten in diese
 * Richtung (Prototyp `tools/history-arrows-prototype`, Variante A). Die Hülle ist ein Radix-Dropdown;
 * sein eigenes Öffnen beim Drücken wird abgefangen — ein einfacher Klick soll springen, nicht listen.
 * Pfeil ↓ auf dem fokussierten Knopf öffnet die Liste wie gewohnt.
 */
const HistoryArrow = ({ direction, enabled, list, onStep, onGoTo }: ArrowProps) => {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  /*
    **Nach dem Drücken schweigt der Tooltip**, bis der Zeiger geht oder die Liste zugeht. Danach wird
    er frisch aufgebaut (`tipKey`): Radix merkte sich sonst sein „offen“ von vor dem Drücken, und der
    Tooltip blieb nach dem Halten stehen (gemeldet 04.10.2026).
  */
  const [pressed, setPressed] = useState(false)
  const [tipKey, setTipKey] = useState(0)
  const releaseTooltip = () => {
    setPressed(false)
    setTipKey(key => key + 1)
  }
  const holdRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // Hat das Halten die Liste geöffnet, springt der Klick beim Loslassen nicht.
  const heldRef = useRef(false)
  const clearHold = () => {
    if (holdRef.current) clearTimeout(holdRef.current)
    holdRef.current = null
  }
  useEffect(() => clearHold, [])

  const label = t(direction === 'back' ? 'titlebar.back' : 'titlebar.forward')
  // Pfeil mit Strich, in derselben Plakette wie ⌘ (die Plakette „back“ ist ein Chevron in anderer Farbe).
  const shortcut = { kind: 'character' as const, text: direction === 'back' ? '←' : '→', mod: true }

  return (
    <DropdownMenu
      open={open}
      onOpenChange={(next) => {
        setOpen(next)
        if (!next) releaseTooltip()
      }}
    >
      <WithTooltip key={tipKey} content={open || pressed ? undefined : label} shortcut={shortcut} side="bottom" delayDuration={HISTORY_TOOLTIP_DELAY_MS}>
        <DropdownMenuTrigger asChild disabled={!enabled}>
          <button
            type="button"
            className={`titlebar__history-arrow titlebar__history-arrow--${direction}${open ? ' titlebar__history-arrow--open' : ''}`}
            aria-label={label}
            onPointerDown={(e) => {
              if (e.button !== 0) return
              // Radix öffnete sonst schon beim Drücken.
              e.preventDefault()
              setPressed(true)
              heldRef.current = false
              clearHold()
              holdRef.current = setTimeout(() => {
                heldRef.current = true
                setOpen(true)
              }, HISTORY_HOLD_MS)
            }}
            onPointerUp={clearHold}
            onPointerLeave={() => {
              clearHold()
              // Bei offener Liste erst beim Schließen — der Auslöser bleibt so lange derselbe.
              if (pressed && !open) releaseTooltip()
            }}
            onClick={() => {
              if (heldRef.current) {
                heldRef.current = false
                return
              }
              onStep()
            }}
            onContextMenu={(e) => {
              e.preventDefault()
              if (enabled) setOpen(true)
            }}
            onKeyDown={(e) => {
              // Enter/Leertaste springen wie der Klick; Radix schaltete sonst die Liste um.
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault()
                onStep()
              }
            }}
          >
            <Chevron direction={direction} />
          </button>
        </DropdownMenuTrigger>
      </WithTooltip>
      <DropdownMenuContent
        align="start"
        className="titlebar__history-menu"
        aria-label={t('titlebar.historyMenu')}
        // Kein Fokus zurück an den Pfeil: Er behielt sonst seinen Ring, bis man woanders klickte
        // (gemeldet 04.10.2026). Die Titlebar ist eine Mausfläche.
        onCloseAutoFocus={(e) => e.preventDefault()}
      >
        {list.map(item => (
          <DropdownMenuItem key={item.index} className="titlebar__history-item" onSelect={() => onGoTo(item.index)}>
            <PageIcon item={item} />
            <span className="titlebar__history-title">{item.title}</span>
            <span className="titlebar__history-space">· {item.lifeSpaceLabel}</span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * **Zurück / Vor** in der Titlebar — der **Seitenverlauf** über alle Lebensbereiche (#225,
 * ADR 0034). macOS neben dem Seitenleisten-Knopf, Windows rechts neben dem Logo; nicht im Web.
 * Ohne Ziel ausgegraut, immer sichtbar. Tooltip „Zurück“/„Vor“ mit ⌘← / ⌘→.
 *
 * @example
 * <HistoryArrows canGoBack canGoForward={false} backList={…} forwardList={[]}
 *   onBack={back} onForward={forward} onGoTo={goTo} />
 */
export const HistoryArrows = React.memo(({ canGoBack, canGoForward, backList, forwardList, onBack, onForward, onGoTo }: HistoryArrowsProps) => (
  <div className="titlebar__history-arrows">
    <HistoryArrow direction="back" enabled={canGoBack} list={backList} onStep={onBack} onGoTo={onGoTo} />
    <HistoryArrow direction="forward" enabled={canGoForward} list={forwardList} onStep={onForward} onGoTo={onGoTo} />
  </div>
))
